import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { ConnectionRunDto, ConnectionRunsResponse } from '../../shared/api-types.js';
import { CONNECTION_SYSTEMS, isOneOf, type ConnectionSystem } from '../../shared/enums.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { DataTable, Icon, Page, PageHead, RelativeTime, StateView, StatusPill, useRequest, type DataTableColumn, type DataTableStatus } from '../ui/index.js';
import {
  RUN_REASON_DIRECTION_WORDS,
  RUN_STATE_DIRECTION_WORDS,
  RUN_TRIGGER_DIRECTION_WORDS,
  durationText,
  movedText,
  RUN_HISTORY_NOUNS,
  retentionText,
  runDurationSeconds,
  runHistoryCaption,
  runHistoryTitle,
  runNoun,
  runsListedText,
  runStatePresentation,
  runStateRank,
  runTriggerPresentation,
  runTriggerRank,
} from './lib/connection-runs.js';
import './pages.css';

// A system's run history (design system 06-modules §6.3 R2, binding from v8.3.0; conformity finding D6 of 2026-10-05,
// fixed as group A by the owner's picks): the person's own recent runs of one system — the Fellow sync or the Slack
// channel-list refresh — newest first, as a table so 04-data §1 binds it: sortable by state and by trigger with words
// that name what the reader gets, the retention window stated on the screen, runs started by hand distinguished by
// word and glyph, and a failed run carrying its failure in the reader's words (§6.5: "from a failed run to its
// failure"). Reached from the system's section on the Connections page and arriving with the system identified in the
// address (§1.9). Below --bp-md the table is a card per row with the sort as a visible control (§6.9).

const CRUMBS = [{ to: PATHS.connections, label: 'Connections' }];

type SortKey = 'started_at' | 'state' | 'trigger' | 'took' | 'moved' | 'reason';

/**
 * The history's columns, every one sortable (04-data §1.2: "Every table is sortable … in every context"; §1.2's only
 * exemptions are row actions, selection, free-form multi-line content and a column whose order is the data — none is
 * here). Exported so the rendering tests can pin that; the page composes them.
 */
export function runHistoryColumns(system: ConnectionSystem): DataTableColumn<ConnectionRunDto>[] {
  return [
    {
      key: 'started_at',
      header: 'Started',
      type: 'date',
      // Why the dates' own words are kept: for a run's start the reader wants "newest first", which is what 'date' says.
      sortValue: (run) => run.startedAt,
      card: 'title',
      cell: (run) => <RelativeTime value={run.startedAt} mode="both" />,
    },
    {
      key: 'state',
      header: 'State',
      type: 'enum',
      directionWords: RUN_STATE_DIRECTION_WORDS,
      sortValue: (run) => runStateRank(run.state),
      card: 'meta',
      cell: (run) => {
        const state = runStatePresentation(run.state);
        return <StatusPill tone={state.tone} icon={state.icon} title={state.detail}>{state.word}</StatusPill>;
      },
    },
    {
      key: 'trigger',
      header: 'Trigger',
      type: 'enum',
      directionWords: RUN_TRIGGER_DIRECTION_WORDS,
      sortValue: (run) => runTriggerRank(run.trigger),
      card: 'meta',
      // A run started by hand is told apart by its word and its glyph alike (§6.3 R2), never by colour.
      cell: (run) => {
        const trigger = runTriggerPresentation(run.trigger);
        return (
          <span className="connection-run-trigger" data-trigger={run.trigger}>
            <Icon name={trigger.icon} size={14} /> {trigger.word}
          </span>
        );
      },
    },
    {
      key: 'took',
      header: 'Took',
      type: 'number',
      numeric: true,
      sortValue: (run) => runDurationSeconds(run),
      card: 'field',
      cell: (run) => {
        const seconds = runDurationSeconds(run);
        return seconds === null ? <span className="muted">running</span> : durationText(seconds);
      },
    },
    {
      key: 'moved',
      header: system === 'fellow' ? 'Meetings' : 'Conversations',
      headerTitle: system === 'fellow' ? 'Meetings stored or refreshed by the run' : 'Conversations listed by the run',
      type: 'number',
      numeric: true,
      sortValue: (run) => run.moved,
      card: 'field',
      cell: (run) => movedText(run) ?? <span className="muted">—</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      // Why sortable, by its code (04-data §1.2; review of 2026-10-05, finding 14): a run's reason is one line from a
      // fixed code→message map plus the code itself — an enumeration, not the free-form multi-line content §1.2 exempts
      // — and a reader wanting every `fellow_unavailable` run together has a natural sort by it. Runs with no reason
      // group at the end in either direction (sort-params.ts). Shown as the failure in the reader's words, with its
      // code selectable for a report (§6.4 Failure; 05-content §4).
      type: 'text',
      directionWords: RUN_REASON_DIRECTION_WORDS,
      sortValue: (run) => run.reason?.code ?? null,
      card: 'field',
      cell: (run) => (run.reason ? (
        <span className="connection-run-reason">
          {run.reason.message} <code className="selectable text-xs">{run.reason.code}</code>
        </span>
      ) : <span className="muted">—</span>),
    },
  ];
}

export default function ConnectionRunsPage() {
  const { system: param } = useParams();
  const system: ConnectionSystem | null = isOneOf(CONNECTION_SYSTEMS, param) ? param : null;
  return system ? <RunHistory system={system} /> : <UnknownSystem />;
}

function UnknownSystem() {
  return (
    <Page measure="reading">
      <PageHead title="Recent runs" crumbs={CRUMBS} />
      <StateView kind="empty" title="No such system" description="Calis records runs for Fellow and Slack. Go back to Connections and open a system's recent runs from its section." minHeight="16rem" />
    </Page>
  );
}

function RunHistory({ system }: { system: ConnectionSystem }) {
  const request = useRequest<ConnectionRunsResponse>((signal) => api.get<ConnectionRunsResponse>(`/api/connections/${system}/runs`, { signal }), [system]);
  const data = request.data;
  const noun = runNoun(system);

  const columns = useMemo(() => runHistoryColumns(system), [system]);

  const status: DataTableStatus = request.status === 'loading' && !data
    ? { kind: 'loading', rowCount: 6 }
    : request.status === 'error' && request.error && !data
      ? { kind: 'error', failure: request.error, onRetry: request.reload }
      : { kind: 'ready' };

  return (
    <Page measure="wide">
      <PageHead
        title={runHistoryTitle(system)}
        crumbs={CRUMBS}
        // The retention window, stated on the screen (06-modules §6.3 R2) — one sentence, this installation's numbers.
        subtitle={data ? retentionText(data) : undefined}
      />
      <DataTable<ConnectionRunDto, SortKey>
        caption={runHistoryCaption(system)}
        failureNoun={RUN_HISTORY_NOUNS[system]}
        columns={columns}
        rows={data?.runs ?? []}
        rowKey={(run) => run.id}
        status={status}
        sort={{ defaultSort: { key: 'started_at', dir: 'desc' } }}
        clientSort
        empty={{ title: `No ${noun} recorded yet`, description: `Runs appear here as they happen — the schedule's and the ones you start by hand.` }}
        rowClassName={(run) => (run.trigger === 'hand' ? 'is-hand-run' : undefined)}
        footer={data ? <p className="text-sm muted">{runsListedText(system, data.runs.length)}</p> : null}
      />
    </Page>
  );
}
